import React, { useEffect, useState, cloneElement } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Badge,
} from "@mui/material";
import { ChevronLeft, HomeOutlined, Person } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/AddOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WalletIcon from "@mui/icons-material/Wallet";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotifications } from "../hooks/useNotifications";
import useWalletStatus from "../hooks/useWalletStatus";
import brandLogo from "assets/icon-white-transparent.png";

const navItems = [
  { text: "Dashboard", icon: <HomeOutlined />, path: "" },
  { text: "My Profile", icon: <Person />, path: "myprofile" },
  { text: "Wallet", icon: <WalletIcon />, path: "wallet" },
  { text: "My Parcels", icon: <AllInboxIcon />, path: "myparcels" },
  { text: "Booking", icon: <AddIcon />, path: "booking" },
  { text: "Tracking", icon: <SearchIcon />, path: "tracking" },
  { text: "Notifications", icon: <NotificationsIcon />, path: "notifications" },
];

const COLLAPSED_WIDTH = "72px";

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

const SidebarCustomer = ({
  customer,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const { walletActive, canActivate, loading: walletLoading } = useWalletStatus();

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  // Hide the Wallet nav item while inactive-wallet status is unknown too, to
  // avoid a flash of the item for customers whose wallet is deactivated. An
  // inactive wallet the customer can self-activate still shows it — the wallet
  // page renders the activation panel then.
  const visibleNavItems = navItems.filter((item) => item.path !== "wallet" || (!walletLoading && (walletActive || canActivate)));

  const renderItem = (text, icon, path, expanded) => {
    const isActive = active === path;
    const sizedIcon = icon ? cloneElement(icon, { sx: { fontSize: 18 } }) : null;
    const badgedIcon = text === "Notifications"
      ? <Badge badgeContent={unreadCount} color="error" max={99}>{sizedIcon}</Badge>
      : sizedIcon;

    const row = (
      <ListItemButton
        onClick={() => {
          navigate(`/${path}`);
          setActive(path);
          if (!isNonMobile) setIsSidebarOpen(false);
        }}
        sx={{
          position: "relative",
          gap: "11px",
          py: "10px",
          px: expanded ? "10px" : "8px",
          mb: "2px",
          borderRadius: "10px",
          justifyContent: expanded ? "flex-start" : "center",
          backgroundColor: isActive ? "rgba(139,92,246,.18)" : "transparent",
          "&:hover": { backgroundColor: "rgba(255,255,255,.07)" },
        }}
      >
        {isActive && (
          <Box sx={{
            position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
            width: "3px", height: "22px", borderRadius: "0 3px 3px 0",
            background: "linear-gradient(180deg,#a78bfa,#6d28d9)",
          }} />
        )}
        <ListItemIcon sx={{
          minWidth: 0, width: 32, height: 32, flex: "none", borderRadius: "9px",
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: isActive ? "rgba(109,40,217,.35)" : "rgba(255,255,255,.06)",
          color: isActive ? "#c4b5fd" : "#6d5e8a",
        }}>
          {badgedIcon}
        </ListItemIcon>
        {expanded && (
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              fontSize: "13px",
              fontWeight: isActive ? 700 : 500,
              color: isActive ? "#e9e3fb" : "#b9b4c0",
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={path} disablePadding sx={{ px: "12px" }}>
        {expanded ? row : (
          <Tooltip title={text} placement="right">
            <Box sx={{ width: "100%" }}>{row}</Box>
          </Tooltip>
        )}
      </ListItem>
    );
  };

  const renderContent = (expanded) => (
    <Box sx={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column",
      overflow: "hidden", background: "linear-gradient(180deg,#1b1728 0%,#14101f 100%)",
    }}>
      {/* brand */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "11px", flex: "none", height: "64px",
        px: expanded ? "20px" : 0, justifyContent: expanded ? "flex-start" : "center",
        borderBottom: "1px solid #2a2244",
      }}>
        <Box sx={{
          width: 34, height: 34, flex: "none", borderRadius: "10px",
          background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(109,40,217,.45)",
        }}>
          <Box
            component="img"
            src={brandLogo}
            alt="Winner Express"
            sx={{ width: 22, height: 22, objectFit: "contain", display: "block" }}
          />
        </Box>
        {expanded && (
          <Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>Winner Express</Typography>
            <Typography sx={{ fontSize: "10px", color: "#6d5e8a", fontWeight: 600, letterSpacing: ".06em", mt: "1px" }}>CUSTOMER PORTAL</Typography>
          </Box>
        )}
      </Box>

      {/* nav */}
      <Box sx={{
        flex: 1, overflowY: "auto", py: "16px",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#3b2f5a", borderRadius: "4px" },
      }}>
        <List disablePadding>
          {visibleNavItems.map(({ text, icon, path }) => renderItem(text, icon, path, expanded))}
        </List>
      </Box>

      {/* footer */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "10px", flex: "none",
        px: expanded ? "16px" : 0, py: "14px", justifyContent: expanded ? "flex-start" : "center",
        borderTop: "1px solid #2a2244",
      }}>
        <Box sx={{
          width: 34, height: 34, flex: "none", borderRadius: "50%",
          background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: "13px",
        }}>
          {initials(customer?.name)}
        </Box>
        {expanded && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "12.5px", fontWeight: 700, color: "#e9e3fb" }}>
                {customer?.name || ""}
              </Typography>
              <Typography sx={{ fontSize: "10.5px", color: "#6d5e8a", fontWeight: 600 }}>
                Customer
              </Typography>
            </Box>
            <IconButton
              onClick={() => setIsSidebarOpen(false)}
              sx={{
                width: 28, height: 28, borderRadius: "8px", flex: "none",
                backgroundColor: "rgba(255,255,255,.06)", border: "1px solid #2a2244",
                "&:hover": { backgroundColor: "rgba(255,255,255,.12)" },
              }}
            >
              <ChevronLeft sx={{ fontSize: "16px", color: "#a094b8" }} />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ flex: "none", height: "100%" }}>
      {isNonMobile ? (
        <Box sx={{
          width: isSidebarOpen ? drawerWidth : COLLAPSED_WIDTH,
          height: "100vh", flex: "none",
          transition: "width .25s cubic-bezier(.4,0,.2,1)",
          overflow: "hidden",
        }}>
          {renderContent(isSidebarOpen)}
        </Box>
      ) : (
        isSidebarOpen && (
          <Drawer
            open={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            variant="temporary"
            anchor="left"
            transitionDuration={{ enter: 500, exit: 500 }}
            sx={{ "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box", border: "none" } }}
          >
            {renderContent(true)}
          </Drawer>
        )
      )}
    </Box>
  );
};

export default SidebarCustomer;
